import { Badge, Option, Search, Select, Theme } from "@deere/fuel-react";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import StarIcon from "@mui/icons-material/Star";
import TimerIcon from "@mui/icons-material/Timer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
} from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { D2DRequestsFilters } from "../../../../../api/d2d/d2dApiInterfaces";
import DownloadButton from "../../../../../components/DataGridPro/DownloadButton/DownloadButton/DownloadButton";
import DateRangePicker from "../../../../../components/DatePickers/DateRangePicker/DateRangePicker";
import LoadingIndicator from "../../../../../components/LoadingIndicator/LoadingIndicator";
import { FeatureToggleContextV2 } from "../../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import i18n from "../../../../../i18n/i18n";
import { generateDataGridLocaleTexts } from "../../../../../utils/dataGridUtils/utils";
import {
  formatDate,
  formatTimeWithLocale,
} from "../../../../../utils/formatters";
import { CATEGORIES, TOGGLES } from "../../../../featureToggles/constants";
import { generateGridDefaultStyles } from "../../../../orders/OrdersByLineItem/utils/table";
import { getD2DRejectStatusLabelKey } from "../../../utils/rejectionStatus";
import { D2D_STATUS_FILTER_OPTIONS, D2DStatusFilterOption } from "../../types";
import { isD2DRequestsFilterApplied } from "../../utils/downloadUtils";
import {
  D2DActionMenu,
  D2DOrderRow,
  D2DStatus,
} from "../D2DActionMenu/D2DActionMenu";

const DEFAULT_TIME_TO_RESPOND_MINUTES = 240;
const TIMEOUT_FIELD_KEY = "timeout_value";
export const EXPIRED_TIME_LABEL = "0:00:00";

export const formatCountdown = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return EXPIRED_TIME_LABEL;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

const isRequestExpired = (
  dateRequested: string | null | undefined,
  timeToRespondMinutes?: number
): boolean => {
  if (!dateRequested) return false;
  const parsed = new Date(dateRequested);
  if (Number.isNaN(parsed.getTime())) return false;
  const minutesToRespond =
    typeof timeToRespondMinutes === "number"
      ? timeToRespondMinutes
      : DEFAULT_TIME_TO_RESPOND_MINUTES;
  const expiryTime = dayjs(parsed).add(minutesToRespond, "minute");
  return expiryTime.diff(dayjs(), "second") <= 0;
};

const CountdownTimer = ({
  dateRequested,
  timeToRespondMinutes,
  onExpire,
}: {
  dateRequested?: string | null;
  timeToRespondMinutes?: number;
  onExpire?: () => void;
}) => {
  const theme = useTheme() as Theme;
  const [timeLeft, setTimeLeft] = useState<string>(EXPIRED_TIME_LABEL);
  const [hasExpired, setHasExpired] = useState(false);

  useEffect(() => {
    const isValidDate = () => {
      if (!dateRequested) return false;
      const parsed = new Date(dateRequested);
      return !Number.isNaN(parsed.getTime());
    };

    const calculateTimeLeft = () => {
      if (!isValidDate()) return EXPIRED_TIME_LABEL;
      const reqDate = dayjs(new Date(dateRequested!));

      const minutesToRespond =
        typeof timeToRespondMinutes === "number"
          ? timeToRespondMinutes
          : DEFAULT_TIME_TO_RESPOND_MINUTES;
      const expiryTime = reqDate.add(minutesToRespond, "minute");
      const now = dayjs();
      const diffInSeconds = expiryTime.diff(now, "second");
      return formatCountdown(diffInSeconds);
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (remaining === EXPIRED_TIME_LABEL && !hasExpired && isValidDate()) {
        setHasExpired(true);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [dateRequested, timeToRespondMinutes, hasExpired, onExpire]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <TimerIcon
        fontSize="small"
        sx={{ color: theme.tokens.FuelColorAlertErrorBorder, opacity: 1 }}
      />
      <Typography
        variant="body2"
        sx={{ color: theme.tokens.FuelColorTextTertiary }}
      >
        {timeLeft}
      </Typography>
    </Box>
  );
};

const formatDateLines = (
  dateStr: string | undefined | null
): { dateLine: string; timeLine: string } => {
  if (!dateStr) return { dateLine: "", timeLine: "" };
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return { dateLine: "", timeLine: "" };
  const formattedDate = formatDate(dateStr, i18n.language, false, true);
  if (formattedDate === "NA") return { dateLine: "", timeLine: "" };
  const dateWithNewFormat = dayjs(dateStr).isValid()
    ? dayjs(dateStr).format("DD-MMM-YYYY")
    : "";
  return {
    dateLine: dateWithNewFormat || formattedDate,
    timeLine: formatTimeWithLocale(dateStr, i18n.language),
  };
};

const sanitizeFilters = (filters: D2DRequestsFilters): D2DRequestsFilters =>
  Object.entries(filters).reduce<D2DRequestsFilters>(
    (sanitized, [key, value]) => {
      if (Array.isArray(value)) {
        return value.length ? { ...sanitized, [key]: value } : sanitized;
      }
      if (typeof value === "string") {
        return value.trim() ? { ...sanitized, [key]: value.trim() } : sanitized;
      }
      if (value) {
        return { ...sanitized, [key]: value };
      }
      return sanitized;
    },
    {}
  );

const StatusChip = ({
  row,
  t,
}: {
  row: D2DOrderRow;
  t: (key: string, defaultValue?: string) => string;
}) => {
  const { status } = row;

  switch (status) {
    case "NEW":
      return (
        <Badge
          startIcon={<StarIcon />}
          variant="info"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.new", "New")}
        </Badge>
      );
    case "ACCEPTED":
      return (
        <Badge
          startIcon={<CheckCircleIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.accepted", "Accepted")}
        </Badge>
      );
    case "REJECTED": {
      const rejectStatusKey =
        getD2DRejectStatusLabelKey({
          status,
          lastChangedBy: row.lastChangedBy,
          rejectionType: row.rejectionType,
          rejectionTrigger: row.rejectionTrigger,
        }) ?? "d2d:status.manualRejected";
      return (
        <Badge
          startIcon={<CancelIcon />}
          variant="error"
          data-testid="d2d-ship-status-badge"
        >
          {t(
            rejectStatusKey,
            rejectStatusKey === "d2d:status.d2dClaimed"
              ? "D2D Claimed"
              : "Manual Reject"
          )}
        </Badge>
      );
    }
    case "EXPIRED":
      return (
        <Badge
          startIcon={<WarningAmberIcon />}
          variant="warning"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.expired", "Expired")}
        </Badge>
      );
    case "SHIPPED":
      return (
        <Badge
          startIcon={<LocalShippingIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.shipped", "Shipped")}
        </Badge>
      );
    case "COMPLETED":
      return (
        <Badge
          startIcon={<CheckCircleIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.completed", "Completed")}
        </Badge>
      );
    case "CREDITED":
      return (
        <Badge
          startIcon={<CheckCircleIcon />}
          variant="success"
          data-testid="d2d-ship-status-badge"
        >
          {t("d2d:status.credited", "Credited")}
        </Badge>
      );
    default:
      return <Badge variant="info">{status}</Badge>;
  }
};

export interface D2DRequestsTableProps {
  rows: D2DOrderRow[];
  loading?: boolean;
  onViewOrderSummary: (row: D2DOrderRow) => void;
  onOpenAcceptModal: (row: D2DOrderRow) => void;
  onOpenRejectModal: (row: D2DOrderRow) => void;
  onOpenShipModal: (row: D2DOrderRow) => void;
  filters?: D2DRequestsFilters;
  onFiltersChange?: (filters: D2DRequestsFilters) => void;
  onClearFilters?: () => void;
  paginationModel?: { page: number; pageSize: number };
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  rowCount?: number;
  paginationMode?: "client" | "server";
  download?: {
    isDownloading: boolean;
    onDownload: () => void;
    tooltipText: string;
  };
}

const D2DRequestsTable = ({
  rows,
  loading = false,
  onViewOrderSummary,
  onOpenAcceptModal,
  onOpenRejectModal,
  onOpenShipModal,
  filters: filtersProp = {},
  onFiltersChange,
  onClearFilters,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  paginationMode = "client",
  download,
}: D2DRequestsTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation(["d2d", "common"]);
  const { additionalFieldsValues } = useContext(FeatureToggleContextV2);
  const timeToRespondMinutes = useMemo(() => {
    const map = additionalFieldsValues(
      CATEGORIES.D2D_SERVICE,
      TOGGLES.ENABLE_D2D_TIME_TO_RESPOND
    );
    if (!map) return DEFAULT_TIME_TO_RESPOND_MINUTES;

    const toPositiveInt = (val?: string): number | undefined => {
      const n = val ? parseInt(val, 10) : NaN;
      return Number.isNaN(n) || n <= 0 ? undefined : n;
    };

    const configured = toPositiveInt(map.get(TIMEOUT_FIELD_KEY)?.[0]);
    return configured ?? DEFAULT_TIME_TO_RESPOND_MINUTES;
  }, [additionalFieldsValues]);

  const [expiredRowIds, setExpiredRowIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<D2DRequestsFilters>(filtersProp);
  const [startFilter, setStartFilter] = useState(false);

  useEffect(() => {
    setFilters(filtersProp);
  }, [filtersProp]);

  useEffect(() => {
    if (!startFilter) return undefined;

    const timeoutId = setTimeout(() => {
      onFiltersChange?.(sanitizeFilters(filters));
      setStartFilter(false);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [filters, onFiltersChange, startFilter]);

  const isFilterApplied = useMemo(
    () => isD2DRequestsFilterApplied(filters),
    [filters]
  );

  const displayRows = useMemo(
    () =>
      rows.map((row) =>
        row.status === "NEW" &&
        (expiredRowIds.has(row.id) ||
          isRequestExpired(row.dateRequested, timeToRespondMinutes))
          ? { ...row, status: "EXPIRED" as D2DStatus }
          : row
      ),
    [rows, expiredRowIds, timeToRespondMinutes]
  );

  const renderTextCell = (params: GridRenderCellParams) => (
    <Typography component="span" sx={{ display: "block", width: "100%" }}>
      {params.value as any}
    </Typography>
  );

  const updateFilter = (key: keyof D2DRequestsFilters, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setStartFilter(true);
  };

  const renderHeaderTextFilter =
    (key: keyof D2DRequestsFilters) => (props: any) =>
      (
        <Search
          data-testid={`d2d-requests-filter-${key}`}
          ref={props?.inputRef}
          placeholder={t("common:table.headerFilter.search.placeholder") || ""}
          sx={{ ".MuiOutlinedInput-input": { textOverflow: "ellipsis" } }}
          value={(filters[key] as string) ?? ""}
          onChange={(e) => updateFilter(key, e.target.value)}
          onClear={() => updateFilter(key, "")}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
            }
          }}
        />
      );

  const renderHeaderDateFilter = () => (
    <DateRangePicker
      label=""
      dateRangeCalendarProps={{
        disableFuture: true,
        minDate: dayjs().subtract(1, "year"),
      }}
      enableShortcuts
      onChange={([from, to]) => {
        setFilters((f) => ({
          ...f,
          dateRequestedFrom: from,
          dateRequestedTo: to,
        }));
        setStartFilter(true);
      }}
      value={[
        filters.dateRequestedFrom ?? null,
        filters.dateRequestedTo ?? null,
      ]}
    />
  );

  const getStatusLabel = (status: D2DStatusFilterOption) => {
    // Map virtual sub-statuses to their existing translation keys
    if (status === "D2D_CLAIMED") {
      return t("d2d:status.d2dClaimed", "D2D Claimed");
    }
    if (status === "MANUAL_REJECTED") {
      return t("d2d:status.manualRejected", "Manual Reject");
    }
    const translationKey = status.toLowerCase();
    return t(`d2d:status.${translationKey}`, status);
  };

  const renderHeaderStatusFilter = () => (
    <Select
      hasSearch
      hasSelectAll
      id="d2d-request-status-dropdown"
      data-testid="d2d-request-status-dropdown"
      multiple
      onChange={(event) => {
        const values = Array.from(
          event.target.selectedOptions ?? [],
          (option) => option.value as D2DStatusFilterOption
        );
        setFilters((f) => ({ ...f, statuses: values }));
        setStartFilter(true);
      }}
      value={filters.statuses || []}
    >
      {D2D_STATUS_FILTER_OPTIONS.map((value: D2DStatusFilterOption) => (
        <Option
          key={value}
          value={value}
          data-testid={`d2d-request-status-filter-${value}`}
        >
          {getStatusLabel(value)}
        </Option>
      ))}
    </Select>
  );

  const columns: GridColDef[] = [
    {
      field: "orderNumber",
      headerName: t("OrderNumber"),
      minWidth: 197,
      renderCell: (params: GridRenderCellParams) => (
        <Typography>{params.value}</Typography>
      ),
      renderHeaderFilter: renderHeaderTextFilter("orderNumber"),
    },
    {
      field: "dealerReference",
      headerName: t("Dealer Reference"),
      minWidth: 197,
      renderCell: renderTextCell,
      renderHeaderFilter: renderHeaderTextFilter("dealerReference"),
    },
    {
      field: "partNumber",
      headerName: t("Part Number"),
      minWidth: 187,
      renderCell: renderTextCell,
      renderHeaderFilter: renderHeaderTextFilter("partNumber"),
    },
    {
      field: "requestedQuantity",
      headerName: t("Requested Qty"),
      minWidth: 187,
      renderCell: renderTextCell,
      renderHeaderFilter: () => <></>,
    },
    {
      field: "acceptedQuantity",
      headerName: t("Accepted Qty"),
      minWidth: 187,
      renderCell: renderTextCell,
      renderHeaderFilter: () => <></>,
    },
    {
      field: "dateRequested",
      headerName: t("Date Requested"),
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => {
        const { dateLine, timeLine } = formatDateLines(params.value as string);
        return (
          <Box>
            <Typography variant="body2">{dateLine}</Typography>
            <Typography variant="caption" color="text.secondary">
              {timeLine}
            </Typography>
          </Box>
        );
      },
      renderHeaderFilter: renderHeaderDateFilter,
    },
    {
      field: "timeToRespond",
      headerName: t("Time To Respond"),
      minWidth: 187,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as D2DOrderRow;
        const status = String(row.status ?? "")
          .toUpperCase()
          .trim();
        if (status === "NEW") {
          return (
            <CountdownTimer
              dateRequested={row.dateRequested}
              timeToRespondMinutes={timeToRespondMinutes}
              onExpire={() =>
                setExpiredRowIds((prev) => new Set(prev).add(row.id))
              }
            />
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{ color: theme.tokens.FuelColorTextTertiary }}
          >
            {String(params.value ?? "")}
          </Typography>
        );
      },
      renderHeaderFilter: () => <></>,
    },
    {
      field: "status",
      headerName: t("Status"),
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const status = String(params.value ?? "")
          .toUpperCase()
          .trim() as D2DStatus;
        return (
          <StatusChip row={{ ...(params.row as D2DOrderRow), status }} t={t} />
        );
      },
      renderHeaderFilter: renderHeaderStatusFilter,
    },
    {
      field: "lastChangedBy",
      headerName: t("Last Changed By"),
      minWidth: 187,
      renderCell: renderTextCell,
      renderHeaderFilter: renderHeaderTextFilter("lastChangedBy"),
    },
    {
      field: "actions",
      headerName: t("Actions"),
      minWidth: 240,
      width: 260,
      flex: 0,
      sortable: false,
      filterable: false,
      renderHeaderFilter: () => <></>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as D2DOrderRow;
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <D2DActionMenu
              request={row}
              onViewOrderSummary={onViewOrderSummary}
            />
            {row.status === "NEW" && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onOpenAcceptModal(row)}
                  sx={{
                    background: "#ffe600",
                    bgcolor: "#ffe600",
                    color: "#222",
                    fontWeight: 600,
                    boxShadow: "none",
                    borderRadius: (theme as Theme).tokens.FuelBorderRadiusMd,
                    "&:hover": { background: "#f5d800" },
                    "&:focus": { background: "#ffe600" },
                    "&.Mui-focusVisible": { background: "#ffe600" },
                    "&:visited": { background: "#ffe600" },
                    outline: "none",
                  }}
                >
                  {t("d2d:d2dRequests.actions.accept", "Accept")}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onOpenRejectModal(row)}
                  sx={{
                    background: "#f2f2f2",
                    color: "#222",
                    fontWeight: 600,
                    boxShadow: "none",
                  }}
                >
                  {t("d2d:d2dRequests.actions.reject", "Reject")}
                </Button>
              </>
            )}
            {row.status === "ACCEPTED" && (
              <Button
                size="small"
                variant="contained"
                onClick={() => onOpenShipModal(row)}
                sx={{
                  background: "#ffe600",
                  bgcolor: "#ffe600",
                  color: "#222",
                  fontWeight: 600,
                  boxShadow: "none",
                  borderRadius: (theme as Theme).tokens.FuelBorderRadiusMd,
                  "&:hover": { background: "#f5d800" },
                  "&:focus": { background: "#ffe600" },
                  "&.Mui-focusVisible": { background: "#ffe600" },
                  "&:visited": { background: "#ffe600" },
                  outline: "none",
                }}
              >
                {t("d2d:d2dRequests.actions.markAsShipped", "Create shipment")}
              </Button>
            )}
          </Box>
        );
      },
    },
  ].map((column) => ({
    ...column,
    headerAlign: "left",
    align: "left",
    minWidth: column.minWidth ?? 187,
    flex: 1,
    renderHeader: (params: GridColumnHeaderParams) => (
      <Typography
        sx={{
          fontWeight: (theme as Theme).tokens.FuelFontWeightSemibold,
          fontSize: (theme as Theme).tokens.FuelFontSizeHeading6,
          marginRight: (theme as Theme).tokens.FuelSpacing1,
        }}
        data-testid={params.field}
      >
        {params.colDef.headerName}
      </Typography>
    ),
  }));

  return (
    <Box sx={{ width: "100%" }}>
      <DataGridPro
        rows={displayRows}
        columns={columns}
        loading={loading}
        getRowHeight={() => "auto"}
        localeText={generateDataGridLocaleTexts(t)}
        sx={{ ...(generateGridDefaultStyles(theme) as any), height: "auto" }}
        style={{ borderRadius: "10px" }}
        data-testid="d2d-requests-table"
        columnBufferPx={columns.length + 1}
        hideFooter
        headerFilters={isFilterApplied || rows.length > -1}
        headerFilterHeight={60}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        paginationMode={paginationMode}
        disableColumnMenu
        disableRowSelectionOnClick
        slots={{
          columnUnsortedIcon: ImportExportIcon,
          columnSortedAscendingIcon: NorthIcon,
          columnSortedDescendingIcon: SouthIcon,
          loadingOverlay: LoadingIndicator,
          toolbar: () => (
            <GridToolbarContainer
              sx={{
                paddingX: theme.tokens.FuelSpacing2,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: theme.tokens.FuelSpacing2,
                }}
              >
                <GridToolbarColumnsButton />
                <Button
                  variant="text"
                  onClick={() => {
                    setFilters({});
                    onClearFilters?.();
                  }}
                  disabled={!isFilterApplied}
                  sx={{
                    paddingX: 0,
                    textTransform: "none",
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    fontSize: theme.tokens.FuelFontSizeSm,
                  }}
                >
                  {t(
                    "orders:allOrders.table.filters.clearButton",
                    "Clear filters"
                  )}
                </Button>
              </Box>
              {download && (
                <DownloadButton
                  disabled={
                    download.isDownloading ||
                    (rows.length === 0 && !isFilterApplied)
                  }
                  onClick={download.onDownload}
                  tooltipText={download.tooltipText}
                  testId="d2d-requests-download-button"
                />
              )}
            </GridToolbarContainer>
          ),
        }}
      />
    </Box>
  );
};

export default D2DRequestsTable;
